import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Banner728x90 {
  id: string;
  user_id: string;
  image_url: string;
  website_url?: string;
  start_date: string;
  end_date: string;
  total_price_cents: number;
  status: 'pending' | 'paid' | 'active' | 'expired' | 'suspended';
  stripe_payment_intent_id?: string;
  stripe_session_id?: string;
  created_at: string;
  updated_at: string;
}

// Fetch active banners
export const useActiveBanners728x90 = () => {
  return useQuery({
    queryKey: ['active-banners-728x90'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('banners_728x90')
        .select('*')
        .eq('status', 'active')
        .lte('start_date', new Date().toISOString().split('T')[0])
        .gte('end_date', new Date().toISOString().split('T')[0]);

      if (error) throw error;
      return data as Banner728x90[];
    },
  });
};

// Fetch all banners (admin)
export const useAllBanners728x90 = () => {
  return useQuery({
    queryKey: ['all-banners-728x90'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('banners_728x90')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Banner728x90[];
    },
  });
};

// CORRECTION: Supprimer cette fonction qui causait des erreurs
// Le calcul du prix se fait maintenant directement dans le composant
export const useCalculateBanner728x90Price = () => {
  return useMutation({
    mutationFn: async (months: number) => {
      // Retourner un prix par défaut sans appeler la BDD
      return months * 5000; // 50€ par mois par défaut
    },
  });
};

// Create banner payment
export const useCreateBannerPayment = () => {
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async (params: {
      months: number;
      image_url: string;
      website_url?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke('create-ad-spot-payment', {
        body: params,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Erreur de paiement',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

// Update banner (admin)
export const useUpdateBanner728x90 = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async ({ bannerId, updates }: { bannerId: string; updates: Partial<Banner728x90> }) => {
      const { error } = await supabase
        .from('banners_728x90')
        .update(updates)
        .eq('id', bannerId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-banners-728x90'] });
      queryClient.invalidateQueries({ queryKey: ['active-banners-728x90'] });
      toast({
        title: 'Succès',
        description: 'Bannière mise à jour',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};

// Delete banner (admin)
export const useDeleteBanner728x90 = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async (bannerId: string) => {
      const { error } = await supabase
        .from('banners_728x90')
        .delete()
        .eq('id', bannerId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-banners-728x90'] });
      queryClient.invalidateQueries({ queryKey: ['active-banners-728x90'] });
      toast({
        title: 'Succès',
        description: 'Bannière supprimée',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};